import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Settings, Info } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export interface LLMSettings {
  model: string;
  systemMessage: string;
  temperature: number;
  topP: number;
  topK: number;
  maxTokens: number;
  stopSequences: string[];
  presencePenalty: number;
  frequencyPenalty: number;
  seed?: number;
  streaming: boolean;
  jsonMode: boolean;
}

import { modelCategories } from '@/config/llm-models';
import { AI_REPLY_CATEGORIZATION_PROMPT } from '@/config/ai-reply-categorization-prompt';

interface LLMSettingsDialogProps {
  settings: LLMSettings;
  onSettingsChange: (settings: LLMSettings) => void;
  context?: 'ai-reply-tagger' | 'tam-analysis' | 'general';
}

export const LLMSettingsDialog: React.FC<LLMSettingsDialogProps> = ({
  settings,
  onSettingsChange,
  context = 'general'
}) => {
  // Initialize settings with context-specific defaults
  const getDefaultSystemMessage = () => {
    if (context === 'ai-reply-tagger') {
      return AI_REPLY_CATEGORIZATION_PROMPT;
    }
    return settings.systemMessage || '';
  };

  // Set default model for ai-reply-tagger context
  const getDefaultModel = () => {
    if (context === 'ai-reply-tagger') {
      return settings.model || 'sonar';
    }
    return settings.model || 'gpt-4o-mini';
  };

  const [localSettings, setLocalSettings] = useState<LLMSettings>({
    ...settings,
    model: getDefaultModel(),
    systemMessage: settings.systemMessage || getDefaultSystemMessage()
  });
  const [open, setOpen] = useState(false);

  const handleSave = () => {
    onSettingsChange(localSettings);
    setOpen(false);
  };

  const updateSetting = (key: keyof LLMSettings, value: any) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleStopSequencesChange = (value: string) => {
    const sequences = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
    updateSetting('stopSequences', sequences);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings className="w-4 h-4" />
          LLM Settings
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>LLM Model Settings</DialogTitle>
        </DialogHeader>
        
        <TooltipProvider>
          <div className="space-y-6">
            <Accordion type="multiple" defaultValue={["core", "sampling"]} className="w-full">
              
              {/* Core Settings */}
              <AccordionItem value="core">
                <AccordionTrigger>Core Settings</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="model">Model</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Choose the AI model that will process your requests. Different models have different costs, speeds, and capabilities.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Select value={localSettings.model} onValueChange={(value) => updateSetting('model', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a model" />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-96">
                          {modelCategories.map((category) => (
                            <div key={category.category} className="space-y-2 mb-4">
                              <div className="px-2 py-1.5">
                                <div className="font-semibold text-sm">{category.category}</div>
                                <div className="text-xs text-muted-foreground">{category.description}</div>
                                <div className="text-xs text-muted-foreground mt-1 italic">{category.useCase}</div>
                              </div>
                              {category.models.map((model) => (
                                <SelectItem 
                                  key={model.value} 
                                  value={model.value} 
                                  className="pl-4 [&>span:first-child]:hidden"
                                >
                                  <div className="w-full flex items-center justify-between">
                                    <div>
                                      <div className="font-medium">{model.label}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {model.provider} • {model.price}
                                      </div>
                                    </div>
                                    {localSettings.model === model.value && (
                                      <div className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                                        Selected
                                      </div>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="systemMessage">System/Developer Message</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Instructions that define how the AI should behave. This is like giving the AI a personality or role to play.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      {context === 'ai-reply-tagger' && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => updateSetting('systemMessage', AI_REPLY_CATEGORIZATION_PROMPT)}
                          className="text-xs"
                        >
                          Load Categorization Prompt
                        </Button>
                      )}
                    </div>
                    <Textarea
                      id="systemMessage"
                      value={localSettings.systemMessage}
                      onChange={(e) => updateSetting('systemMessage', e.target.value)}
                      placeholder="Instructions for the AI model behavior..."
                      rows={6}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="jsonMode"
                      checked={localSettings.jsonMode}
                      onCheckedChange={(checked) => updateSetting('jsonMode', checked)}
                    />
                    <div className="flex items-center gap-2">
                      <Label htmlFor="jsonMode">JSON Mode (Structured Response)</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Forces the AI to respond in a structured JSON format. Great for consistent data processing and automated workflows.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="streaming"
                      checked={localSettings.streaming}
                      onCheckedChange={(checked) => updateSetting('streaming', checked)}
                    />
                    <div className="flex items-center gap-2">
                      <Label htmlFor="streaming">Streaming Response</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Show the AI response word-by-word as it's generated, like ChatGPT. Better user experience for longer responses.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Sampling & Creativity */}
              <AccordionItem value="sampling">
                <AccordionTrigger>Sampling & Creativity</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="temperature">Temperature</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Controls creativity vs consistency.<br/>0 = deterministic, 2 = very creative</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm text-muted-foreground">{localSettings.temperature}</span>
                    </div>
                    <Slider
                      id="temperature"
                      min={0}
                      max={2}
                      step={0.1}
                      value={[localSettings.temperature]}
                      onValueChange={(value) => updateSetting('temperature', value[0])}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">0 = deterministic, 2 = very creative</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="topP">Top-p (Nucleus Sampling)</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Alternative to temperature.<br/>0.1 = very focused (top 10% words)<br/>0.9 = diverse (top 90% words)</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm text-muted-foreground">{localSettings.topP}</span>
                    </div>
                    <Slider
                      id="topP"
                      min={0}
                      max={1}
                      step={0.05}
                      value={[localSettings.topP]}
                      onValueChange={(value) => updateSetting('topP', value[0])}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="topK">Top-k (Limit Candidate Tokens)</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Limits AI to top K most likely words.<br/>50 = moderate focus<br/>10 = very predictable</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      id="topK"
                      type="number"
                      value={localSettings.topK}
                      onChange={(e) => updateSetting('topK', parseInt(e.target.value) || 0)}
                      placeholder="0 = disabled"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Length & Stops */}
              <AccordionItem value="length">
                <AccordionTrigger>Length & Stop Controls</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="maxTokens">Max Completion Tokens</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Maximum length of AI response.<br/>1000 tokens ≈ 750 words<br/>Higher = longer but more expensive</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      id="maxTokens"
                      type="number"
                      value={localSettings.maxTokens}
                      onChange={(e) => updateSetting('maxTokens', parseInt(e.target.value) || 1000)}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="stopSequences">Stop Sequences (comma-separated)</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Words/phrases that make AI stop generating.<br/>Useful for controlling format and length.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      id="stopSequences"
                      value={localSettings.stopSequences.join(', ')}
                      onChange={(e) => handleStopSequencesChange(e.target.value)}
                      placeholder="e.g., ###, STOP, [END]"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Repetition & Style */}
              <AccordionItem value="repetition">
                <AccordionTrigger>Repetition & Style Controls</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="presencePenalty">Presence Penalty</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Encourages new topics vs repeating ideas.<br/>Positive = more diverse topics</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm text-muted-foreground">{localSettings.presencePenalty}</span>
                    </div>
                    <Slider
                      id="presencePenalty"
                      min={-2}
                      max={2}
                      step={0.1}
                      value={[localSettings.presencePenalty]}
                      onValueChange={(value) => updateSetting('presencePenalty', value[0])}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Encourage new topics (positive values)</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="frequencyPenalty">Frequency Penalty</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Prevents repeating same words too often.<br/>Positive = less repetitive language</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm text-muted-foreground">{localSettings.frequencyPenalty}</span>
                    </div>
                    <Slider
                      id="frequencyPenalty"
                      min={-2}
                      max={2}
                      step={0.1}
                      value={[localSettings.frequencyPenalty]}
                      onValueChange={(value) => updateSetting('frequencyPenalty', value[0])}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Discourage repeated tokens (positive values)</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Determinism */}
              <AccordionItem value="determinism">
                <AccordionTrigger>Determinism & Performance</AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="seed">Seed (for reproducibility)</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Makes AI give same response every time.<br/>Useful for testing and consistency.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      id="seed"
                      type="number"
                      value={localSettings.seed || ''}
                      onChange={(e) => updateSetting('seed', e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="Leave empty for random"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </TooltipProvider>

        <Separator />
        
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Apply Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};